import React, { useState, useEffect, useRef } from 'react';
import { Send, AlertCircle, CheckCircle } from 'lucide-react';
import { Dialog } from '../ui/Dialog.js';
import { Button } from '../ui/Button.js';
import { Field, Input, Textarea } from '../ui/Field.js';
import { TuningPicker } from '../tuning/TuningPicker.js';
import { Link, useLocation } from 'react-router-dom';
import { apiClient, ApiError } from '../../utils/apiClient.js';
import { useAuthStore } from '../../stores/authStore.js';
import { TabRequestItem } from './RequestRow.js';

export interface TabRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onRequestSubmitted: (data?: { duplicate: boolean; request: TabRequestItem }) => void;
}

export const TabRequestDialog: React.FC<TabRequestDialogProps> = ({
  open,
  onClose,
  onRequestSubmitted
}) => {
  const { isAuthenticated, isGuest } = useAuthStore();
  const verifyDialogOpen = useAuthStore((s) => s.verifyDialogOpen);
  const openVerifyDialog = useAuthStore((s) => s.openVerifyDialog);
  const isUnauthorized = !isAuthenticated || isGuest;
  const location = useLocation();
  // FND-10: sign-in links return to this page (validated again by AuthPage via safeRedirect).
  const authHref = `/auth?from=${encodeURIComponent(location.pathname + location.search)}`;
  const submittingRef = useRef(false);
  const closeTimerRef = useRef<number | null>(null);
  const [needsVerify, setNeedsVerify] = useState(false);

  const [songTitle, setSongTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [tuning, setTuning] = useState('E Standard');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    songTitle?: string;
    artist?: string;
    referenceUrl?: string;
  }>({});

  useEffect(() => {
    if (!open) {
      setSongTitle('');
      setArtist('');
      setTuning('E Standard');
      setReferenceUrl('');
      setNotes('');
      setErrorMsg(null);
      setSuccessMsg(null);
      setNeedsLogin(false);
      setLoading(false);
      setFieldErrors({});
      setNeedsVerify(false);
      submittingRef.current = false;
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Blocks double submits, including during the post-success close delay.
    if (submittingRef.current) return;

    const trimmedUrl = referenceUrl.trim();
    const errs: typeof fieldErrors = {};
    if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
      errs.referenceUrl = 'نشانی باید با http:// یا https:// شروع شود.';
    }
    if (!songTitle.trim()) errs.songTitle = 'عنوان الزامی است.';
    if (!artist.trim()) errs.artist = 'نام هنرمند الزامی است.';
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setErrorMsg(null);
      return;
    }

    const { isAuthenticated, isGuest } = useAuthStore.getState();
    if (!isAuthenticated || isGuest) {
      setNeedsLogin(true);
      setErrorMsg('برای ثبت درخواست باید وارد حساب کاربری شوید.');
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setErrorMsg(null);
    setNeedsLogin(false);
    setNeedsVerify(false);
    setFieldErrors({});
    let succeeded = false;

    try {
      const res = await apiClient.post<{ duplicate: boolean; request: TabRequestItem }>(
        '/api/tabs/requests',
        {
          song_title: songTitle.trim(),
          artist: artist.trim(),
          tuning,
          reference_url: trimmedUrl || undefined,
          notes: notes.trim() || undefined
        }
      );

      succeeded = true;
      setSuccessMsg('درخواست شما ثبت شد و در لیست نگارش قرار گرفت.');
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null;
        onRequestSubmitted(res);
        onClose();
      }, 600);
    } catch (err: unknown) {
      console.warn('Tab request failed:', err);
      if (err instanceof ApiError) {
        if (err.status === 403 && err.data?.code === 'EMAIL_NOT_VERIFIED') {
          // apiClient already opened the verify dialog; keep the form and explain inline.
          setNeedsVerify(true);
          setErrorMsg('برای ثبت درخواست ابتدا ایمیل خود را تایید کنید.');
        } else if (err.status === 401) {
          setNeedsLogin(true);
          setErrorMsg('برای ثبت درخواست باید وارد حساب کاربری شوید.');
        } else if (err.status === 400) {
          if (err.data?.details && typeof err.data.details === 'object') {
            const d = err.data.details as Record<string, any>;
            const mapped: typeof fieldErrors = {};
            if (d.song_title) mapped.songTitle = d.song_title._errors?.[0] || 'عنوان نامعتبر است.';
            if (d.artist) mapped.artist = d.artist._errors?.[0] || 'نام هنرمند نامعتبر است.';
            if (d.reference_url) {
              mapped.referenceUrl = 'نشانی باید با http:// یا https:// شروع شود.';
            }
            setFieldErrors(mapped);
            if (Object.keys(mapped).length > 0) {
              setErrorMsg(null);
              return;
            }
          }
          setErrorMsg('درخواست ثبت نشد؛ اطلاعات واردشده را بررسی کنید.');
        } else if (err.status === 409) {
          setErrorMsg('این درخواست قبلاً ثبت شده است.');
        } else if (err.status >= 500) {
          setErrorMsg('خطای سرور رخ داد؛ درخواست ثبت نشد. بعداً دوباره تلاش کنید.');
        } else {
          setErrorMsg('درخواست ثبت نشد؛ اطلاعات واردشده را بررسی کنید.');
        }
      } else {
        setErrorMsg('ارتباط با سرور برقرار نشد؛ درخواست ثبت نشد. اتصال اینترنت را بررسی کنید.');
      }
    } finally {
      // Stay locked after success until the dialog closes (reset on `open` -> false).
      if (!succeeded) submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog
      // Hidden (not reset: the reset effect keys on the `open` prop) while the global verify dialog is shown, so
      // the verify dialog is never stacked under this one.
      open={open && !verifyDialogOpen}
      onClose={onClose}
      title="درخواست نگارش تبلچر جدید"
      titleHeadingLevel="h3"
      description="اگر تبلچر مورد نظرتان در بازارچه نیست، مشخصات آن را ثبت کنید."
      size="md"
      data-testid="tab-request-dialog"
      footer={
        <>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={Boolean(successMsg)}
            leadingIcon={<Send className="size-4" />}
            data-testid="dialog-primary-action"
          >
            ارسال درخواست
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
            data-testid="dialog-secondary-action"
          >
            انصراف
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5 pb-6">
        {isUnauthorized && !errorMsg && (
          <div
            role="alert"
            className="flex items-center gap-2 p-3 text-xs rounded-xl bg-warning/15 border border-warning/30 text-warning-soft"
          >
            <AlertCircle className="size-4 shrink-0 text-warning" />
            <span>
              برای ثبت درخواست باید وارد حساب کاربری شوید.{' '}
              <Link to={authHref} className="link link-primary font-semibold">
                ورود به حساب
              </Link>
            </span>
          </div>
        )}

        {errorMsg && (
          <div
            role="alert"
            className="flex items-center gap-2 p-3 text-xs rounded-xl bg-error/15 border border-error/30 text-error-soft"
          >
            <AlertCircle className="size-4 shrink-0" />
            <span>
              {errorMsg}
              {needsLogin && (
                <>
                  {' '}
                  <Link to={authHref} className="link link-primary font-semibold">
                    ورود به حساب
                  </Link>
                </>
              )}
              {needsVerify && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={openVerifyDialog}
                    data-testid="tab-request-verify-link"
                    className="link link-primary font-semibold"
                  >
                    تایید ایمیل
                  </button>
                </>
              )}
            </span>
          </div>
        )}

        {successMsg && (
          <div
            role="status"
            className="flex items-center gap-2 p-3 text-xs rounded-xl bg-success/15 border border-success/30 text-success-soft"
          >
            <CheckCircle className="size-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        <Field label="نام قطعه / آهنگ" required error={fieldErrors.songTitle}>
          <Input
            type="text"
            placeholder="مثال: Tornado of Souls"
            value={songTitle}
            onChange={(e) => {
              setSongTitle(e.target.value);
              setFieldErrors((prev) => ({ ...prev, songTitle: undefined }));
            }}
            maxLength={200}
            error={Boolean(fieldErrors.songTitle)}
            required
          />
        </Field>

        <Field label="هنرمند یا گروه" required error={fieldErrors.artist}>
          <Input
            type="text"
            placeholder="مثال: Megadeth"
            value={artist}
            onChange={(e) => {
              setArtist(e.target.value);
              setFieldErrors((prev) => ({ ...prev, artist: undefined }));
            }}
            maxLength={200}
            error={Boolean(fieldErrors.artist)}
            required
          />
        </Field>

        <Field label="نوع گیتار و کوک احتمالی">
          <TuningPicker value={tuning} onChange={setTuning} testIdPrefix="request-tuning" />
        </Field>

        <Field label="لینک مرجع (YouTube یا Spotify اختیاری)" error={fieldErrors.referenceUrl}>
          <Input
            type="url"
            placeholder="https://..."
            value={referenceUrl}
            onChange={(e) => {
              setReferenceUrl(e.target.value);
              setFieldErrors((prev) => ({ ...prev, referenceUrl: undefined }));
            }}
            maxLength={500}
            error={Boolean(fieldErrors.referenceUrl)}
            dir="ltr"
            isLatin
          />
        </Field>

        <Field label="توضیحات و نکات (اختیاری)" id="tab-request-notes">
          <Textarea
            id="tab-request-notes"
            aria-label="توضیحات و نکات (اختیاری)"
            placeholder="مثال: لطفاً سولو با دقت بالا نت‌نویسی شود..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </Field>
      </form>
    </Dialog>
  );
};
